import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Building2, Mail, User, Phone, ShieldCheck, Globe2, Search,
  FileBarChart2, Sparkles, AlertCircle, CheckCircle2, ArrowRight, Loader2,
  RefreshCw,
} from 'lucide-react';

const API_BASE = '';

export default function PocSignupPage() {
  const navigate = useNavigate();
  const [companyName, setCompanyName] = useState('');
  const [companyEmail, setCompanyEmail] = useState('');
  const [contactName, setContactName] = useState('');
  const [phone, setPhone] = useState('');
  const [country, setCountry] = useState('');
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);
  const [mailSent, setMailSent] = useState(false);

  useEffect(() => {
    try {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
      if (tz.includes('Kolkata') || tz.includes('Calcutta')) setCountry('IN');
      else if (tz.includes('Los_Angeles') || tz.includes('New_York')) setCountry('US');
      else if (tz.includes('London')) setCountry('GB');
      else if (tz.includes('Paris')) setCountry('FR');
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    const email = companyEmail.trim().toLowerCase();
    if (!email.includes('@') || email.length < 5) {
      setPreview(null);
      return;
    }
    const t = setTimeout(() => {
      fetch(`${API_BASE}/api/poc/signup?email=${encodeURIComponent(email)}`)
        .then((r) => r.json())
        .then((d) => setPreview(d))
        .catch(() => setPreview(null));
    }, 400);
    return () => clearTimeout(t);
  }, [companyEmail]);

  useEffect(() => {
    if (!done || !mailSent) return undefined;
    const t = setTimeout(() => navigate('/login'), 8000);
    return () => clearTimeout(t);
  }, [done, mailSent, navigate]);

  const canSubmit = useMemo(() => {
    return (
      !!companyName.trim() &&
      !!companyEmail.trim() &&
      !!contactName.trim() &&
      preview?.allowed !== false &&
      !busy &&
      !done
    );
  }, [companyName, companyEmail, contactName, preview, busy, done]);

  async function onSubmit(e) {
    e.preventDefault();
    setError('');
    if (!companyName.trim() || !companyEmail.trim() || !contactName.trim()) {
      setError('Company name, company email, and your name are required.');
      return;
    }
    setBusy(true);
    try {
      const res = await fetch(`${API_BASE}/api/poc/signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          company_name: companyName.trim(),
          company_email: companyEmail.trim().toLowerCase(),
          contact_name: contactName.trim(),
          phone: phone.trim(),
          signup_country: country,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data.error || data.message || 'Signup failed. Please try again.');
        return;
      }
      setMailSent(data.email_sent !== false);
      setDone(true);
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setBusy(false);
    }
  }

  const inputClass =
    'w-full px-4 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#1B365D] focus:border-transparent transition-shadow disabled:opacity-50';

  return (
    <div className="min-h-screen flex bg-gradient-to-br from-[#0f2240] via-[#1B365D] to-[#1B365D]">

      {/* Left — brand + all marketing copy (same text, login-page styling) */}
      <div className="hidden lg:flex lg:w-[54%] flex-col justify-between px-8 py-10 xl:px-10 xl:py-12 relative overflow-hidden">
        <div className="absolute -top-24 -left-24 w-96 h-96 rounded-full bg-[#1B365D]/60 blur-3xl" />
        <div className="absolute bottom-0 right-0 w-80 h-80 rounded-full bg-[#F5A623]/20 blur-3xl" />

        <div className="relative z-10 flex items-center gap-4 min-w-0">
          <div className="bg-white rounded-lg px-2 py-1 shadow-sm shrink-0">
            <img
              src="/logo/PayLegit.png"
              alt="PayLegit"
              className="h-11 w-auto object-contain"
              onError={(e) => { e.currentTarget.style.display = 'none'; }}
            />
          </div>
          <div className="min-w-0 border-l border-white/30 pl-4">
            <div className="font-bold text-white text-base xl:text-lg leading-tight">MCC as a Service</div>
            <div className="text-sm xl:text-[15px] text-blue-100/95 font-medium leading-snug mt-0.5">
              Automated MCC for Online Merchants
            </div>
          </div>
        </div>

        <div className="relative z-10 space-y-5 my-8 w-full pr-2">
          <div className="inline-flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide text-[#F5A623] bg-[#F5A623]/15 border border-[#F5A623]/30 rounded-full px-2.5 py-1">
            <Sparkles className="w-3 h-3" /> Live merchant intelligence
          </div>

          <h1 className="text-3xl xl:text-4xl font-extrabold text-white leading-tight tracking-tight w-full">
            Turn merchant websites into Mastercard
            <br />
            <span className="text-blue-100">&amp; Visa</span>{' '}
            <span className="text-[#F5A623]">MCC codes</span>
            <span className="text-white"> — automatically</span>
          </h1>

          <p className="text-blue-100/90 text-sm leading-relaxed w-full">
            PayLegit scrapes merchant URLs for products and services, About Us pages, and
            business descriptions. A <strong className="text-white">trained LLM</strong>{' '}
            analyses that evidence and assigns Merchant Category Codes aligned with Mastercard
            &amp; Visa catalogs — fast enough for a POC, rigorous enough for payments teams.
            Trial covers up to <strong className="text-white">10 merchants</strong>.
          </p>

          <ul className="space-y-2.5 text-sm text-blue-100/95 w-full">
            <li className="flex gap-2.5 items-start">
              <span className="mt-0.5 flex h-7 w-7 items-center justify-center rounded-lg bg-white/10 text-blue-100 border border-white/15 shrink-0">
                <Globe2 className="w-3.5 h-3.5" />
              </span>
              <span><b className="text-white">Crawl URL</b> — scrape the merchant website for current signals</span>
            </li>
            <li className="flex gap-2.5 items-start">
              <span className="mt-0.5 flex h-7 w-7 items-center justify-center rounded-lg bg-white/10 text-blue-100 border border-white/15 shrink-0">
                <Search className="w-3.5 h-3.5" />
              </span>
              <span><b className="text-white">Extract</b> — products, services, About Us &amp; business description</span>
            </li>
            <li className="flex gap-2.5 items-start">
              <span className="mt-0.5 flex h-7 w-7 items-center justify-center rounded-lg bg-[#F5A623]/20 text-[#F5A623] border border-[#F5A623]/30 shrink-0">
                <Sparkles className="w-3.5 h-3.5" />
              </span>
              <span><b className="text-white">Classify</b> — trained LLM maps evidence to Mastercard &amp; Visa MCC</span>
            </li>
            <li className="flex gap-2.5 items-start">
              <span className="mt-0.5 flex h-7 w-7 items-center justify-center rounded-lg bg-emerald-400/20 text-emerald-300 border border-emerald-400/25 shrink-0">
                <FileBarChart2 className="w-3.5 h-3.5" />
              </span>
              <span><b className="text-white">Deliver</b> — review &amp; download reports for up to <b className="text-white">10 merchants</b> on this trial</span>
            </li>
          </ul>

          <div className="rounded-xl border border-white/15 bg-white/5 backdrop-blur-sm p-4 w-full">
            <div className="flex items-start gap-2.5">
              <RefreshCw className="w-4 h-4 text-[#F5A623] shrink-0 mt-0.5" />
              <div className="min-w-0 flex-1">
                <h2 className="text-xs font-semibold text-white mb-1">
                  Why fresh website signals matter for MCC
                </h2>
                <p className="text-xs text-blue-100/80 leading-relaxed">
                  Some classification approaches lean on historic tax or registration narratives
                  (for example older VAT / GST descriptions). Those records are useful context —
                  but they are often static and may no longer match how a merchant actually
                  trades today. Card schemes expect MCC to reflect the <em>current</em> nature
                  of the business. PayLegit therefore prioritises live website signals —
                  products, About Us, and the latest online description — so categories stay
                  aligned with how the business presents itself now.
                </p>
              </div>
            </div>
          </div>

          <p className="text-[11px] text-blue-200/80 flex items-center gap-1.5 flex-wrap">
            <ShieldCheck className="w-3.5 h-3.5 text-blue-300" />
            Corporate email required · Need more than 10 merchants?{' '}
            <a className="text-[#F5A623] hover:underline font-medium" href="mailto:Contact@paylegit.in">
              Contact@paylegit.in
            </a>
          </p>
        </div>

        <p className="relative z-10 text-blue-400 text-xs">
          &copy; {new Date().getFullYear()} PayLegit. All rights reserved.
        </p>
      </div>

      {/* Right — signup form (same panel style as login) */}
      <div className="flex-1 flex items-center justify-center p-6 sm:p-8 bg-white lg:rounded-l-3xl lg:shadow-2xl overflow-y-auto">
        <div className="w-full max-w-sm py-4">

          <div className="flex lg:hidden items-center gap-3 mb-8">
            <img
              src="/logo/PayLegit.png"
              alt="PayLegit"
              className="h-10 w-auto object-contain"
              onError={(e) => { e.currentTarget.style.display = 'none'; }}
            />
            <div className="min-w-0 border-l border-slate-200 pl-3">
              <div className="font-bold text-slate-900 text-sm leading-tight">MCC as a Service</div>
              <div className="text-xs text-slate-600 font-medium leading-snug">Automated MCC for Online Merchants</div>
            </div>
          </div>

          <h1 className="text-2xl font-bold text-slate-900 mb-1">Start your MCC POC</h1>
          <p className="text-slate-500 text-sm mb-6">
            Use your organization email. Free providers (Gmail, Hotmail, etc.) are not accepted.
          </p>

          <form onSubmit={onSubmit} className="space-y-4" autoComplete="off">
            <div>
              <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1.5">
                <span className="inline-flex items-center gap-1.5">
                  <Building2 className="w-3 h-3" /> Company name *
                </span>
              </label>
              <input
                className={inputClass}
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                placeholder="Acme Payments Pvt Ltd"
                required
                disabled={busy || done}
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1.5">
                <span className="inline-flex items-center gap-1.5">
                  <Mail className="w-3 h-3" /> Company email *
                </span>
              </label>
              <input
                type="email"
                className={inputClass}
                value={companyEmail}
                onChange={(e) => setCompanyEmail(e.target.value)}
                placeholder="name@yourcompany.com"
                required
                disabled={busy || done}
              />
            </div>

            {preview?.message && (
              <div className={`text-[11px] rounded-xl px-3 py-2 flex gap-2 ${
                preview.allowed === false
                  ? 'bg-red-50 text-red-700 border border-red-100'
                  : 'bg-amber-50 text-amber-800 border border-amber-100'
              }`}>
                <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                <span>{preview.message || preview.error}</span>
              </div>
            )}
            {preview?.error && !preview.message && (
              <div className="text-[11px] rounded-xl px-3 py-2 bg-red-50 text-red-700 border border-red-100 flex gap-2">
                <AlertCircle className="w-3.5 h-3.5 shrink-0" /> {preview.error}
              </div>
            )}

            <div>
              <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1.5">
                <span className="inline-flex items-center gap-1.5">
                  <User className="w-3 h-3" /> Your name *
                </span>
              </label>
              <input
                className={inputClass}
                value={contactName}
                onChange={(e) => setContactName(e.target.value)}
                placeholder="Full name"
                required
                disabled={busy || done}
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1.5">
                <span className="inline-flex items-center gap-1.5">
                  <Phone className="w-3 h-3" /> Phone / Mobile
                </span>
              </label>
              <input
                className={inputClass}
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="+1 …"
                disabled={busy || done}
              />
            </div>

            {error && (
              <div className="text-xs text-red-700 bg-red-50 border border-red-100 rounded-xl px-3 py-2 flex gap-2">
                <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" /> {error}
              </div>
            )}
            {done && mailSent && (
              <div className="text-sm text-emerald-900 bg-emerald-50 border border-emerald-100 rounded-xl px-3 py-3 space-y-2">
                <p className="font-semibold flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 shrink-0" /> Your POC account is ready
                </p>
                <p>
                  Please check your inbox for an email from PayLegit with your sign-in details.
                </p>
                <p>
                  If you do not see it within a few minutes, check your spam or junk folder and
                  mark the message as not spam.
                </p>
                <p className="text-xs text-emerald-800/80">Opening the sign-in page shortly.</p>
              </div>
            )}
            {done && !mailSent && (
              <div className="text-sm text-amber-900 bg-amber-50 border border-amber-100 rounded-xl px-3 py-3 space-y-2">
                <p className="font-semibold flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 shrink-0" /> Account created
                </p>
                <p>
                  We could not send the sign-in email. Please write to{' '}
                  <a className="underline" href="mailto:Contact@paylegit.in">Contact@paylegit.in</a>
                  {' '}and we will help you sign in.
                </p>
              </div>
            )}

            <button
              type="submit"
              disabled={!canSubmit || preview?.allowed === false}
              className="w-full py-3 bg-gradient-to-r from-[#F5A623] to-[#E8941E] hover:from-[#E8941E] hover:to-[#d4840f] text-white text-sm font-bold rounded-xl shadow-lg transition-all disabled:opacity-50 mt-2 inline-flex items-center justify-center gap-2"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
              Create POC account
            </button>
          </form>

          <p className="text-center text-xs text-slate-400 mt-6">
            Protected by enterprise-grade security.<br />
            Your data is encrypted in transit and at rest.
          </p>
          <p className="text-center text-sm text-slate-600 mt-4">
            Already have an account?{' '}
            <Link to="/login" className="text-blue-700 font-medium hover:underline">
              Sign in
            </Link>
          </p>

          {/* Mobile-only: compact copy of key left-panel notes */}
          <div className="lg:hidden mt-8 pt-6 border-t border-slate-100 space-y-3">
            <p className="text-[11px] text-slate-500 flex items-center gap-1.5 flex-wrap">
              <ShieldCheck className="w-3 h-3 text-slate-400" />
              Corporate email required · Need more than 10 merchants?{' '}
              <a className="text-blue-700 hover:underline font-medium" href="mailto:Contact@paylegit.in">
                Contact@paylegit.in
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
